import { Global } from '@emotion/react'
import React from 'react'

import {
  AlignItems,
  BorderStyle,
  Box,
  Button,
  ButtonColorVariant,
  ButtonVariant,
  Color,
  Display,
  DisplayPosition,
  FontWeight,
  Icon,
  IconVariant,
  JustifyContent,
  Modal,
  Space,
  Spinner,
  Text,
} from '@gousto-internal/citrus-react'
import type { StandardRecipeItem } from '@library/api-menu-experience'
import { DeviceType, useDeviceType } from '@library/dom'
import type { RecipeUUID } from '@library/menu-service'
import { AddRecipeButton, ExperienceIngredientOptions } from '@library/recipe-customisations'

import type {
  RecipeTileActionProps,
  RecipeTileCustomisationData,
  RecipeTileCustomisationSelection,
} from './RecipeTile.types'
import { RecipeTileImageSection } from './RecipeTileImageSection'
import { RecipeTitle } from './RecipeTileBody/RecipeTitle'
import { SubtitleTagRow } from './RecipeTileBody/SubtitleTagRow'

type Props = {
  recipe: StandardRecipeItem
  customisationData?: RecipeTileCustomisationData
  onClose?: () => void
  onSelectCustomisation?: (selection: RecipeTileCustomisationSelection) => void
  onRetry?: () => void
} & RecipeTileActionProps

type ShellProps = Pick<Props, 'recipe' | 'onClose'> & { children: React.ReactNode }

const CUSTOMISATION_MODAL_PORTAL_CLASS = 'recipe-tile-customisation-modal-portal'
const PREVIEW_SUBTITLE_TAGS = ['cookingTime', 'energy']
const stopPropagation = (event: React.SyntheticEvent) => event.stopPropagation()

const drawerModalStyle = {
  overlay: {
    alignItems: 'flex-end',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1000,
  },
  content: {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
    bottom: 0,
    inset: 'auto 0 0 0',
    left: 0,
    padding: 0,
    position: 'absolute',
    right: 0,
    top: 'auto',
    width: '100%',
  },
} as const

const getCustomisationOptions = (
  customisations: NonNullable<Props['customisationData']>['customisations'],
) => {
  const availabilityByRecipeId = new Map<RecipeUUID, boolean>()

  customisations?.slots.forEach((slot) => {
    slot.values.forEach(({ recipeId, isAvailable }) => {
      if (recipeId) {
        availabilityByRecipeId.set(
          recipeId,
          isAvailable && (availabilityByRecipeId.get(recipeId) ?? true),
        )
      }
    })
  })

  return Array.from(availabilityByRecipeId, ([recipeId, isAvailable]) => ({
    recipe: { id: recipeId },
    isOutOfStock: !isAvailable,
  }))
}

const getSelectedPricing = (data: RecipeTileCustomisationData) =>
  data.customisations?.slots
    .flatMap((slot) => slot.values)
    .find((value) => value.recipeId === data.selectedRecipeId)?.pricing

const DrawerPreview = ({ recipe }: Pick<Props, 'recipe'>) => {
  const previewRecipe = {
    ...recipe,
    tagGroups: {
      ...recipe.tagGroups,
      subtitle: recipe.tagGroups.subtitle?.filter(({ type }) =>
        PREVIEW_SUBTITLE_TAGS.includes(type),
      ),
    },
  }

  return (
    <Box data-testid="recipe-tile-customisation-drawer-preview">
      <RecipeTileImageSection
        recipe={recipe}
        isOutOfStock={!recipe.isAvailable}
        lazy={false}
        insideOverflow
        imageVariant="prominent"
      />
      <Box bg={Color.White} paddingH={4} paddingV={3}>
        <RecipeTitle title={recipe.title} />
        <Space size={2} />
        <SubtitleTagRow recipe={previewRecipe} />
      </Box>
    </Box>
  )
}

const Shell = ({ children, recipe, onClose }: ShellProps) => {
  const isMobile = useDeviceType() === DeviceType.MOBILE

  if (!isMobile) {
    return (
      <Box
        bg={Color.White}
        borderColor={Color.ColdGrey_200}
        borderStyle={BorderStyle.Solid}
        borderTopWidth={0.5}
        boxShadow="0 18px 14px 0 rgba(51, 61, 71, 0.2)"
        data-testid="recipe-tile-customisation-dropdown"
        left="0"
        onClick={stopPropagation}
        onKeyDown={stopPropagation}
        paddingH={4}
        paddingV={4}
        position={DisplayPosition.Absolute}
        right="0"
        top="100%"
        style={{ zIndex: 40 }}
      >
        {children}
      </Box>
    )
  }

  const appElement =
    typeof document === 'undefined' ? undefined : document.getElementById('react-root') ?? undefined

  return (
    <>
      <Global
        styles={{
          [`.${CUSTOMISATION_MODAL_PORTAL_CLASS} .ReactModal__Content > div`]: { width: '100%' },
        }}
      />
      <Modal
        {...(appElement ? { appElement } : { ariaHideApp: false })}
        contentLabel="Customise recipe"
        isOpen
        name="recipe-tile-customisation-modal"
        onRequestClose={onClose}
        portalClassName={CUSTOMISATION_MODAL_PORTAL_CLASS}
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        style={drawerModalStyle}
      >
        <Box
          bg={Color.White}
          data-testid="recipe-tile-customisation-dropdown"
          maxHeight="100dvh"
          onClick={stopPropagation}
          style={{ overflowY: 'auto' }}
          width="100vw"
        >
          <Box
            alignItems={AlignItems.Center}
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            paddingH={4}
            paddingV={3}
          >
            <Text fontWeight={FontWeight.SemiBold} size={5}>
              Customise
            </Text>
            <Button
              aria-label="Close customisation"
              colorVariant={ButtonColorVariant.Tertiary}
              data-testid="recipe-tile-customisation-close"
              icon="close"
              onClick={onClose}
              type="button"
              variant={ButtonVariant.Icon}
            />
          </Box>
          <DrawerPreview recipe={recipe} />
          <Box paddingBottom={4} paddingH={4} paddingV={2}>
            {children}
          </Box>
        </Box>
      </Modal>
    </>
  )
}

const Content = ({
  recipe,
  quantity = 0,
  canAddRecipes,
  reachedLimit,
  onAddClick,
  onRemoveClick,
  onBlockedAddAttempt,
  customisationData,
  onClose,
  onSelectCustomisation,
  onRetry,
}: Props) => {
  if (customisationData?.isLoadingCustomisations) {
    return (
      <Box display={Display.Flex} justifyContent={JustifyContent.Center} paddingV={4} width="100%">
        <Spinner color={Color.ColdGrey_900} size={6} />
      </Box>
    )
  }

  if (
    customisationData?.hasCustomisationError ||
    !customisationData?.customisations ||
    !customisationData.customisationRecipe
  ) {
    return <Text size={2}>Customisation unavailable</Text>
  }

  const isPending = Boolean(customisationData.isCustomisationSelectionPending)
  const pricing = getSelectedPricing(customisationData) ?? recipe.pricing

  return (
    <>
      <Box data-testid="recipe-tile-customisation-options">
        <ExperienceIngredientOptions
          menuId={customisationData.menuId ?? null}
          currentCollectionId={customisationData.collectionId}
          view="view_recipe_list_screen"
          recipeUuid={customisationData.customisationRecipe.id}
          selectedRecipeId={
            customisationData.selectedRecipeId ?? customisationData.customisationRecipe.id
          }
          options={getCustomisationOptions(customisationData.customisations)}
          ingredientSlots={customisationData.customisations.slots}
          isDisabled={isPending}
          onSelectRecipe={(recipeId, recipeUrn) => onSelectCustomisation?.({ recipeId, recipeUrn })}
        />
      </Box>
      {quantity > 0 ? (
        <Button
          colorVariant={ButtonColorVariant.Secondary}
          disabled={isPending}
          onClick={() => onRemoveClick(recipe.id)}
          width="100%"
        >
          <Icon name="delete" variant={IconVariant.Actionable} />
          <Space direction="horizontal" size={1} />
          Remove recipe
        </Button>
      ) : (
        <AddRecipeButton
          canAddRecipes={canAddRecipes}
          isOutOfStock={!recipe.isAvailable}
          isNegativeSurcharge={Boolean(pricing?.isNegative)}
          onClickAdd={() => {
            if (!canAddRecipes) {
              onBlockedAddAttempt()
              return
            }
            onAddClick(recipe.id)
            onClose?.()
          }}
          onClickRemove={() => onRemoveClick(recipe.id)}
          quantity={quantity}
          reachedLimit={reachedLimit || isPending}
          surchargeLabel={pricing?.perPortionSurcharge ?? null}
        />
      )}
      {customisationData.hasCustomisationSelectionError && (
        <>
          <Space size={4} />
          <Box
            alignItems={AlignItems.Center}
            bg={Color.ColdGrey_900}
            data-testid="recipe-tile-customisation-selection-error"
            display={Display.Flex}
            justifyContent={JustifyContent.SpaceBetween}
            paddingH={3}
            paddingV={3}
            role="alert"
          >
            <Text color={Color.White} fontWeight={FontWeight.Bold} size={2}>
              We couldn&apos;t apply that customisation.
            </Text>
            <Button colorVariant={ButtonColorVariant.Tertiary} onClick={onRetry} type="button">
              Try again
            </Button>
          </Box>
        </>
      )}
    </>
  )
}

export const RecipeTileCustomisationDropdown = (props: Props) => (
  <Shell recipe={props.recipe} onClose={props.onClose}>
    <Content {...props} />
  </Shell>
)
